import { is } from '@client/helpers/dom.guards';
import { replaceTextAndKeepTextSelection } from '@client/helpers/dom.helpers';
import { shouldFillEmptyFieldOnArrowRight } from '@client/helpers/empty-field-fill.helpers';
import { useIsReadonly } from '@client/hooks/use-user.hooks';
import { useEllipsisStyles } from '@ui/EllipsisText/ellipsis.styles'; // bad bad import
import { NodonTheme } from '@client/style';
import { KeyboardKeys } from '@shared/models/keyboard.model';
import { OutlinedInputProps, useTheme } from '@mui/material';
import { TypographyVariant } from '@mui/material/styles';
import React, {
  ChangeEvent,
  FocusEvent,
  KeyboardEvent,
  MouseEvent,
  useCallback,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { makeStyles } from 'tss-react/mui';
import { useTimeout } from 'usehooks-ts';

import NodonTextField, { NodonTextFieldProps } from './NodonTextField';

const useStyles = makeStyles<void, 'noborder'>()((
  { palette: { text, neutral, custom } },
  _params,
  classes,
) => {
  const strikeThroughLine = {
    content: '""',
    position: 'absolute' as const,
    top: '50%',
    transform: 'translateY(-50%)',
    zIndex: 1,
    height: 2,
    right: 0,
    left: 0,
    background: text.secondary,
  };

  return {
    root: {
      '&:hover:not(.Mui-focused):not(.Mui-disabled):not(.Mui-readOnly) .MuiOutlinedInput-notchedOutline':
        {
          borderColor: neutral.lighter,
        },
      // noborder hides the idle outline; keep a ring only while the field is focused
      '&.Mui-focused:not(.Mui-readOnly) .MuiOutlinedInput-notchedOutline': {
        borderColor: custom.focus,
      },

      // Disabled is like readonly, but without graying out the text
      '&.Mui-disabled': {
        WebkitTextFillColor: neutral.light,
      },
      // Prevent pointer events and user selection when disabled or readonly
      '&.Mui-disabled, &.Mui-readOnly': {
        borderColor: 'transparent !important',
        pointerEvents: 'none',
        userSelect: 'none',

        [`.${classes.noborder}`]: {
          borderColor: 'transparent !important',
        },
      },
    },
    inactive: {
      WebkitTextFillColor: neutral.light,
    },
    noborder: {
      borderColor: 'transparent',
    },
    input: {
      fontSize: NodonTheme.typography.fontSize,
      padding: '8.5px 8px',
    },
    label: {
      fontSize: NodonTheme.typography.fontSize,
      left: -6,
      '&.Mui-focused': {
        left: 0,
      },
    },
    strikeThrough: {
      '&::placeholder': {
        textDecoration: 'line-through !important',
      },
      '&::after': {
        ...strikeThroughLine,
        left: 3,
      },
    },
    italic: {
      fontStyle: 'italic',
    },
    pointer: {
      cursor: 'pointer',
    },
    tabStyles: {
      textAlign: 'center',
      textTransform: 'uppercase',
      fontSize: '0.875rem',
    },
  };
});

export interface InlineTextFieldProps {
  value: string;
  /** controls if border is visible and editing is possible. focuses the input on changing from anything -> true */
  defaultValue?: string;
  /**
   * Editable text the empty field fills on ArrowRight, when it must differ
   * from the grayed {@link defaultValue} placeholder (e.g. a numeric field whose
   * placeholder is a formatted "7.85 × 10¹²" but which must fill an editable
   * plain number). Falls back to {@link defaultValue} when unset.
   */
  fillValue?: string;
  variant?: TypographyVariant;
  textFieldProps?: Partial<NodonTextFieldProps>;

  editing?: boolean;
  tabInput?: boolean;
  displayBorder?: boolean;
  error?: boolean;

  /**
   * Like disabled, but without graying out the text.
   */
  readonly?: boolean;

  /**
   * Grayed out without input possibility to edit.
   */
  disabled?: boolean;

  /**
   * Grayed out without affecting input possibility to edit.
   */
  inactive?: boolean;

  /**
   * Make input width 100%
   */
  fullWidth?: boolean;

  allowClickEventWhenDisabled?: boolean;

  /**
   * Focus the input when input is added to the DOM.
   */
  autoFocus?: boolean;

  /**
   * When editing starts, select the full value (handy for replacing a number).
   */
  selectOnFocus?: boolean;

  /**
   * Increment to focus the field and select its text again, for example after
   * a rejected value.
   */
  reselectToken?: number;

  /**
   * Make input width adjust to text width.
   */
  autoWidth?: boolean;

  /** Override the default minimum width used by auto-width inputs. */
  autoWidthMin?: number;

  /** Override the horizontal padding reserved by auto-width measurement. */
  autoWidthPadding?: number;

  /**
   * Make text bold.
   */
  bold?: boolean;

  /**
   * Apply strikethrough to the text.
   */
  strikeThrough?: boolean;

  /**
   * Apply italic to the text.
   */
  italic?: boolean;

  onCancel?: () => void;
  onClick?: (e: MouseEvent) => void;
  onDoubleClick?: (e: MouseEvent) => void;
  onSave?: (value: string) => void;
  onChange?: (value: string) => void;
  onKeyDown?: (e: KeyboardEvent) => void;

  /**
   * Formatter user input while editing.
   * One use case is to make property names valid without showing error.
   */
  formatInput?: (value: string) => string;
}

const noBubble = (e: FocusEvent | MouseEvent | KeyboardEvent): void => {
  e.preventDefault();
  e.stopPropagation();
};

/**
 * True when focus moved to a control that must not end the inline edit
 * (undo snackbar action, expression fx button).
 */
const shouldKeepInlineEdit = (target: EventTarget | null): boolean => {
  if (!is.HTMLElement(target)) return false;
  return target.closest('[data-keep-inline-edit]') !== null;
};

const InlineTextField: React.FC<InlineTextFieldProps> = ({
  value,
  defaultValue,
  fillValue,
  variant,
  textFieldProps,
  editing: editingParam,
  autoFocus = true,
  selectOnFocus = false,
  reselectToken = 0,
  tabInput,
  displayBorder,
  error,
  disabled,
  inactive,
  readonly: readonlyParam,
  strikeThrough,
  allowClickEventWhenDisabled,
  fullWidth,
  autoWidth,
  autoWidthMin,
  autoWidthPadding,
  bold,
  italic,
  onCancel,
  onClick,
  onDoubleClick,
  onSave,
  onChange,
  onKeyDown,
  formatInput = defaultFormatInput,
}) => {
  // Hook checks if user can edit project at all. After that we check that if this is disabled or readonly.
  const readonly = useIsReadonly() || disabled || readonlyParam || false;
  const editing = !readonly && !!editingParam;
  const { classes, cx } = useStyles();
  const { typography } = useTheme();
  const ellipsis = useEllipsisStyles({});
  const [localValue, setLocalValue] = useState(value);
  const wasEditing = useRef(editing);

  const inputRef = useRef<HTMLInputElement | undefined>(undefined);
  const skipBlurCommitRef = useRef(false);
  const shouldDeferFocus = editing && !readonly && autoFocus;

  const variantStyle: React.CSSProperties = useMemo(() => {
    if (!variant || !typography[variant]) {
      return {};
    }

    const { fontFamily, fontWeight, fontSize, lineHeight, letterSpacing } =
      typography[variant];

    return { fontFamily, fontWeight, fontSize, lineHeight, letterSpacing };
  }, [typography, variant]);

  useTimeout(
    () => {
      const input = inputRef.current;
      if (!input) return;
      input.focus();
      if (selectOnFocus) input.select();
    },
    shouldDeferFocus ? 0 : null,
  );

  useLayoutEffect(() => {
    if (!reselectToken) return;
    const input = inputRef.current;
    if (!input) return;
    input.focus();
    input.select();
  }, [reselectToken]);

  // Pick up an edit-specific representation exactly when editing starts, while
  // still ignoring subsequent parent-value flicker so keystrokes are preserved.
  useLayoutEffect(() => {
    const editingStarted = editing && !wasEditing.current;
    wasEditing.current = editing;
    if (!editing || editingStarted) setLocalValue(value);
  }, [value, editing]);

  const handleClick = useCallback(
    (e: MouseEvent<HTMLInputElement>) => {
      if (editing) {
        noBubble(e);
      }
      if (allowClickEventWhenDisabled || !disabled) {
        onClick?.(e);
      }
    },
    [allowClickEventWhenDisabled, disabled, editing, onClick],
  );

  const handleChange = useCallback(
    (e: ChangeEvent<HTMLInputElement>) => {
      const value = e.target.value;
      const formattedValue = formatInput(value);
      // If the value has changed, replace the text but keep the text selection
      if (formattedValue !== value) {
        replaceTextAndKeepTextSelection(e.target, formattedValue);
      }
      setLocalValue(formattedValue);
      onChange?.(formattedValue);
    },
    [formatInput, onChange],
  );

  const handleKeyDown = useCallback(
    (e: KeyboardEvent<HTMLInputElement>) => {
      // Forward first so callers can preventDefault to suppress the internal
      // Escape/Enter swallow (typeahead wrappers need Up/Down/Enter to drive
      // their own selection).
      onKeyDown?.(e);
      if (e.defaultPrevented) return;
      // ArrowRight in an empty field pulls the grayed-out default into the
      // input so the user can build on it (e.g. "Gypsum" -> "Gypsum XX") instead
      // of retyping. `fillValue` overrides the placeholder when the latter isn't
      // directly editable (a formatted number). Tab still moves focus. ArrowRight
      // with existing text is left to the browser so the caret can step.
      const filled = fillValue ?? defaultValue;
      if (
        editing &&
        shouldFillEmptyFieldOnArrowRight(e, localValue) &&
        filled
      ) {
        e.preventDefault();
        setLocalValue(filled);
        onChange?.(filled);
        // Place caret after the filled text once the controlled value has rendered.
        // Use the input ref (e.currentTarget may be MUI's root, not the <input>).
        requestAnimationFrame(() =>
          inputRef.current?.setSelectionRange(filled.length, filled.length),
        );
        return;
      }
      if (
        editing &&
        (e.key === KeyboardKeys.ESCAPE || e.key === KeyboardKeys.ENTER)
      ) {
        return noBubble(e);
      }
    },
    [editing, onKeyDown, localValue, defaultValue, fillValue, onChange],
  );

  const handleCancel = useCallback(() => {
    setLocalValue(value);
    onCancel?.();
  }, [onCancel, value]);

  const handleKeyUp = useCallback(
    (e: KeyboardEvent<HTMLInputElement>) => {
      if (editing) {
        if (e.key === KeyboardKeys.ENTER) {
          noBubble(e);
          // Blur commits through handleBlur. Skip that path so Enter saves once.
          skipBlurCommitRef.current = true;
          if (localValue !== value) onSave?.(localValue);
          else handleCancel();
          inputRef.current?.blur();
        }
        if (e.key === KeyboardKeys.ESCAPE) {
          noBubble(e);
          handleCancel();
        }
      }
    },
    [editing, handleCancel, onSave, localValue, value],
  );

  const handleBlur = useCallback(
    (e: FocusEvent<HTMLInputElement>) => {
      // Unmount (version/proposal bar swap) fires blur with a detached input;
      // treating that as cancel would drop a rename hint before the field remounts.
      if (!e.currentTarget.isConnected) return;
      if (skipBlurCommitRef.current) {
        skipBlurCommitRef.current = false;
        return;
      }
      if (!editing || disabled) return;
      // Focus moving to a control that opts to preserve the inline edit (e.g. the
      // fx button that opens the expression panel) must not commit/cancel here —
      // that would tear the edit down and unmount the control before its own click
      // handler runs.
      if (shouldKeepInlineEdit(e.relatedTarget)) return;
      noBubble(e);
      if (localValue !== value && !error) {
        onSave?.(localValue);
        return;
      }
      handleCancel();
    },
    [editing, disabled, localValue, value, error, handleCancel, onSave],
  );

  const inputProps: Partial<OutlinedInputProps> = useMemo(
    () => ({
      ...textFieldProps?.InputProps,
      inputProps: {
        ...textFieldProps?.InputProps?.inputProps,
        style: {
          ...textFieldProps?.InputProps?.inputProps?.style,
          // Apply typography directly to the input element to honor variants.
          ...variantStyle,
        },
      },
      readOnly: readonly,
      disabled,
      classes: {
        root: cx(classes.root, inactive && classes.inactive),
        notchedOutline: !displayBorder ? classes.noborder : undefined,
        input: cx(
          !displayBorder && !editing ? classes.pointer : undefined,
          tabInput && classes.tabStyles,
          !editing && ellipsis.classes.ellipsis,
          italic && classes.italic,
          strikeThrough && !editing && classes.strikeThrough,
          classes.input,
        ),
      },
      style: variantStyle,
    }),
    [
      textFieldProps?.InputProps,
      disabled,
      readonly,
      editing,
      cx,
      classes.root,
      classes.inactive,
      classes.noborder,
      classes.pointer,
      classes.tabStyles,
      classes.strikeThrough,
      classes.italic,
      classes.input,
      inactive,
      displayBorder,
      tabInput,
      ellipsis.classes.ellipsis,
      strikeThrough,
      italic,
      variantStyle,
    ],
  );

  return (
    <NodonTextField
      {...textFieldProps}
      inputRef={inputRef}
      variant="outlined"
      size="small"
      InputProps={inputProps}
      className={cx(
        strikeThrough && localValue !== '' && !editing && classes.strikeThrough,
      )}
      bold={bold}
      autoWidth={autoWidth}
      autoWidthMin={autoWidthMin}
      autoWidthPadding={autoWidthPadding}
      fullWidth={fullWidth}
      disabled={!!disabled}
      placeholder={defaultValue}
      value={localValue}
      error={error}
      onChange={handleChange}
      onKeyDown={handleKeyDown}
      onKeyUp={handleKeyUp}
      onBlur={handleBlur}
      onClick={handleClick}
      onDoubleClick={onDoubleClick}
    />
  );
};

/**
 * Default formatter for the value.
 */
const defaultFormatInput = (value: string) => value.replace(/^\s+/g, ''); // Remove leading whitespace (not trailing)

export default InlineTextField;
